import { supabase } from '../lib/supabase'

// Helper function to generate public URL from storage path
const getAttachmentUrl = (storagePath) => {
  if (!storagePath) return null
  return `https://uaooghxtslvyaizttavi.supabase.co/storage/v1/object/public/article-attachments/${storagePath}`
}

// Article Service - Handles all article-related operations
export const articleService = {
  // Get all published articles with optional filtering
  async getPublishedArticles(filters = {}) {
    try {
      let query = supabase
        .from('articles')
        .select('*')
        .eq('status', 'published')
        .order('published_at', { ascending: false })

      // Apply filters
      if (filters.category && filters.category !== 'all') {
        query = query.eq('category', filters.category)
      }
      
      if (filters.difficulty && filters.difficulty !== 'all') {
        query = query.eq('difficulty', filters.difficulty)
      }
      
      if (filters.featured !== undefined) {
        query = query.eq('featured', filters.featured)
      }
      
      if (filters.search) {
        query = query.or(`title.ilike.%${filters.search}%,excerpt.ilike.%${filters.search}%,content.ilike.%${filters.search}%`)
      }
      
      if (filters.tags && filters.tags.length > 0) {
        query = query.overlaps('tags', filters.tags)
      }
      
      if (filters.limit) {
        query = query.limit(filters.limit)
      }

      const { data, error } = await query
      
      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching published articles:', error)
      throw error
    }
  },

  // Get all articles (including drafts) - for admin/editor use
  async getAllArticles(filters = {}) {
    try {
      let query = supabase
        .from('articles')
        .select('*')
        .order('updated_at', { ascending: false })

      // Apply filters
      if (filters.category && filters.category !== 'all') {
        query = query.eq('category', filters.category)
      }
      
      if (filters.difficulty && filters.difficulty !== 'all') {
        query = query.eq('difficulty', filters.difficulty)
      }
      
      if (filters.status && filters.status !== 'all') {
        query = query.eq('status', filters.status)
      }
      
      if (filters.featured !== undefined) {
        query = query.eq('featured', filters.featured)
      }
      
      if (filters.search) {
        query = query.or(`title.ilike.%${filters.search}%,excerpt.ilike.%${filters.search}%,content.ilike.%${filters.search}%`)
      }
      
      if (filters.tags && filters.tags.length > 0) {
        query = query.overlaps('tags', filters.tags)
      }
      
      if (filters.limit) {
        query = query.limit(filters.limit)
      }

      // Apply sorting
      if (filters.sortBy) {
        const ascending = filters.sortOrder === 'asc'
        query = query.order(filters.sortBy, { ascending })
      }

      const { data, error } = await query
      
      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching all articles:', error)
      throw error
    }
  },

  // Get user's own articles (including drafts)
  async getUserArticles() {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('author_id', user.id)
        .order('updated_at', { ascending: false })
      
      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching user articles:', error)
      throw error
    }
  },

  // Get single article by ID
  async getArticleById(id) {
    try {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('id', id)
        .single()
      
      if (error) throw error
      return data
    } catch (error) {
      console.error('Error fetching article:', error)
      throw error
    }
  },

  // Create new article
  async createArticle(articleData) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      // Get user profile for author name
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', user.id)
        .single()

      const article = {
        ...articleData,
        author_id: user.id,
        author_name: profile?.full_name || user.email,
        reading_time: estimateReadingTime(articleData.content),
        published_at: articleData.status === 'published' ? new Date().toISOString() : null
      }

      const { data, error } = await supabase
        .from('articles')
        .insert([article])
        .select()
        .single()
      
      if (error) throw error
      return data
    } catch (error) {
      console.error('Error creating article:', error)
      throw error
    }
  },

  // Update article
  async updateArticle(id, updates) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      // Update reading time if content changed
      if (updates.content) {
        updates.reading_time = estimateReadingTime(updates.content)
      }

      // Set published_at if status changed to published
      if (updates.status === 'published' && !updates.published_at) {
        updates.published_at = new Date().toISOString()
      }

      const { data, error } = await supabase
        .from('articles')
        .update(updates)
        .eq('id', id)
        .eq('author_id', user.id) // Ensure user can only update their own articles
        .select()
        .single()
      
      if (error) throw error
      return data
    } catch (error) {
      console.error('Error updating article:', error)
      throw error
    }
  },

  // Delete article
  async deleteArticle(id) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      const { error } = await supabase
        .from('articles')
        .delete()
        .eq('id', id)
        .eq('author_id', user.id) // Ensure user can only delete their own articles
      
      if (error) throw error
      return true
    } catch (error) {
      console.error('Error deleting article:', error)
      throw error
    }
  },

  // Record article view for analytics
  async recordView(articleId, userAgent = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      const viewData = {
        article_id: articleId,
        user_id: user?.id || null,
        user_agent: userAgent || navigator.userAgent
      }

      // Insert view record
      const { error: viewError } = await supabase
        .from('article_views')
        .insert([viewData])
      
      if (viewError) console.warn('Error recording view:', viewError)

      // Increment view count using the database function
      const { error: countError } = await supabase
        .rpc('increment_article_views', { article_uuid: articleId })
      
      if (countError) console.warn('Error incrementing view count:', countError)
      
      return true
    } catch (error) {
      console.warn('Error recording article view:', error)
      return false
    }
  },

  // Like/unlike article
  async toggleLike(articleId) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      // Check if user already liked this article
      const { data: existingLike } = await supabase
        .from('article_likes')
        .select('id')
        .eq('article_id', articleId)
        .eq('user_id', user.id)
        .single()

      if (existingLike) {
        // Unlike - remove like
        const { error } = await supabase
          .from('article_likes')
          .delete()
          .eq('article_id', articleId)
          .eq('user_id', user.id)
        
        if (error) throw error

        // Decrement like count
        await supabase
          .from('articles')
          .update({ likes: supabase.raw('likes - 1') })
          .eq('id', articleId)
        
        return { liked: false }
      } else {
        // Like - add like
        const { error } = await supabase
          .from('article_likes')
          .insert([{ article_id: articleId, user_id: user.id }])
        
        if (error) throw error

        // Increment like count
        await supabase
          .from('articles')
          .update({ likes: supabase.raw('likes + 1') })
          .eq('id', articleId)
        
        return { liked: true }
      }
    } catch (error) {
      console.error('Error toggling article like:', error)
      throw error
    }
  },

  // Check if user liked an article
  async checkUserLike(articleId) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return false

      const { data } = await supabase
        .from('article_likes')
        .select('id')
        .eq('article_id', articleId)
        .eq('user_id', user.id)
        .single()
      
      return !!data
    } catch (error) {
      return false
    }
  },

  // Get user likes for multiple articles (batch operation)
  async getUserLikes(articleIds) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user || !articleIds || articleIds.length === 0) return []

      const { data, error } = await supabase
        .from('article_likes')
        .select('article_id')
        .eq('user_id', user.id)
        .in('article_id', articleIds)
      
      if (error) throw error
      return data ? data.map(like => like.article_id) : []
    } catch (error) {
      console.error('Error fetching user likes:', error)
      return []
    }
  },

  // Get featured articles
  async getFeaturedArticles(limit = 5) {
    try {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('status', 'published')
        .eq('featured', true)
        .order('published_at', { ascending: false })
        .limit(limit)
      
      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching featured articles:', error)
      throw error
    }
  },

  // Get popular articles (by views)
  async getPopularArticles(limit = 5) {
    try {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('status', 'published')
        .order('views', { ascending: false })
        .limit(limit)
      
      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching popular articles:', error)
      throw error
    }
  },

  // Get recent articles
  async getRecentArticles(limit = 5) {
    try {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('status', 'published')
        .order('published_at', { ascending: false })
        .limit(limit)
      
      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching recent articles:', error)
      throw error
    }
  },

  // Search articles with advanced options
  async searchArticles(searchTerm, options = {}) {
    try {
      let query = supabase
        .from('articles')
        .select('*')
        .eq('status', 'published')

      if (searchTerm) {
        query = query.or(`title.ilike.%${searchTerm}%,excerpt.ilike.%${searchTerm}%,content.ilike.%${searchTerm}%`)
      }

      if (options.category) {
        query = query.eq('category', options.category)
      }

      if (options.difficulty) {
        query = query.eq('difficulty', options.difficulty)
      }

      if (options.tags && options.tags.length > 0) {
        query = query.overlaps('tags', options.tags)
      }

      if (options.dateFrom) {
        query = query.gte('published_at', options.dateFrom)
      }

      if (options.dateTo) {
        query = query.lte('published_at', options.dateTo)
      }

      // Sorting
      const sortBy = options.sortBy || 'published_at'
      const sortOrder = options.sortOrder || 'desc'
      query = query.order(sortBy, { ascending: sortOrder === 'asc' })

      if (options.limit) {
        query = query.limit(options.limit)
      }

      const { data, error } = await query
      
      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error searching articles:', error)
      throw error
    }
  },

  // Get article analytics
  async getArticleAnalytics(articleId) {
    try {
      // Get basic article info
      const { data: article } = await supabase
        .from('articles')
        .select('views, likes, created_at, published_at')
        .eq('id', articleId)
        .single()

      // Get view history (last 30 days)
      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

      const { data: viewHistory } = await supabase
        .from('article_views')
        .select('viewed_at')
        .eq('article_id', articleId)
        .gte('viewed_at', thirtyDaysAgo.toISOString())
        .order('viewed_at', { ascending: true })

      // Get like history
      const { data: likeHistory } = await supabase
        .from('article_likes')
        .select('created_at')
        .eq('article_id', articleId)
        .order('created_at', { ascending: true })

      return {
        ...article,
        viewHistory: viewHistory || [],
        likeHistory: likeHistory || []
      }
    } catch (error) {
      console.error('Error fetching article analytics:', error)
      throw error
    }
  },

  // File Attachment Methods
  
  // Upload file attachment for article
  async uploadAttachment(articleId, file, options = {}) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      // Validate file
      const maxSize = 10 * 1024 * 1024 // 10MB
      if (file.size > maxSize) {
        throw new Error('File size must be less than 10MB')
      }

      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain']
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Only PDF, DOC, DOCX, and TXT files are allowed')
      }

      // Generate unique filename
      const fileExt = file.name.split('.').pop()
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`
      const filePath = `articles/${articleId}/attachments/${fileName}`

      // Upload file to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('article-attachments')
        .upload(filePath, file)

      if (uploadError) {
        console.error('Storage upload error:', uploadError)
        throw new Error(`Failed to upload file: ${uploadError.message}`)
      }

      // Generate public URL from storage path
      const publicUrl = getAttachmentUrl(filePath)

      // Save attachment record to database
      const attachmentData = {
        article_id: articleId,
        file_name: file.name,
        file_url: publicUrl,
        file_type: file.type,
        file_size: file.size,
        storage_path: filePath,
        display_name: options.displayName || file.name,
        upload_order: options.uploadOrder || 0,
        is_primary: options.isPrimary || false,
        uploaded_by: user.id
      }

      const { data, error } = await supabase
        .from('article_attachments')
        .insert([attachmentData])
        .select()
        .single()

      if (error) {
        console.error('Database insert error:', error)
        // Clean up uploaded file if database insert fails
        await supabase.storage
          .from('article-attachments')
          .remove([filePath])
        throw new Error(`Failed to save attachment record: ${error.message}`)
      }
      
      return data
    } catch (error) {
      console.error('Error uploading attachment:', error)
      throw error
    }
  },

  // Get article attachments
  async getArticleAttachments(articleId) {
    try {
      const { data, error } = await supabase
        .from('article_attachments')
        .select('*')
        .eq('article_id', articleId)
        .order('upload_order', { ascending: true })
        .order('created_at', { ascending: true })

      if (error) throw error
      
      // Add generated URLs from storage_path
      const attachmentsWithUrls = (data || []).map(attachment => ({
        ...attachment,
        file_url: getAttachmentUrl(attachment.storage_path) || attachment.file_url
      }))
      
      return attachmentsWithUrls
    } catch (error) {
      console.error('Error fetching article attachments:', error)
      throw error
    }
  },

  // Get article with attachments
  async getArticleWithAttachments(articleId) {
    try {
      const { data, error } = await supabase
        .rpc('get_article_with_attachments', { article_uuid: articleId })

      if (error) throw error
      
      if (data && data.length > 0) {
        return {
          ...data[0].article_data,
          attachments: data[0].attachments_data
        }
      }
      
      return null
    } catch (error) {
      console.error('Error fetching article with attachments:', error)
      throw error
    }
  },

  // Update attachment
  async updateAttachment(attachmentId, updates) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      const { data, error } = await supabase
        .from('article_attachments')
        .update(updates)
        .eq('id', attachmentId)
        .eq('uploaded_by', user.id)
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error updating attachment:', error)
      throw error
    }
  },

  // Delete attachment
  async deleteAttachment(attachmentId) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      // Get attachment info first
      const { data: attachment, error: fetchError } = await supabase
        .from('article_attachments')
        .select('storage_path, uploaded_by, file_name')
        .eq('id', attachmentId)
        .single()

      if (fetchError) throw new Error(`Failed to fetch attachment: ${fetchError.message}`)
      if (!attachment) throw new Error('Attachment not found')
      if (attachment.uploaded_by !== user.id) throw new Error('Unauthorized to delete this attachment')

      // Delete file from storage using storage_path
      const { error: storageError } = await supabase.storage
        .from('article-attachments')
        .remove([attachment.storage_path])

      if (storageError) {
        console.warn('Error deleting file from storage:', storageError)
        // Continue with database deletion even if storage deletion fails
      }

      // Delete attachment record from database
      const { error: deleteError } = await supabase
        .from('article_attachments')
        .delete()
        .eq('id', attachmentId)
        .eq('uploaded_by', user.id)

      if (deleteError) throw new Error(`Failed to delete attachment record: ${deleteError.message}`)
      
      return true
    } catch (error) {
      console.error('Error deleting attachment:', error)
      throw error
    }
  },

  // Set primary attachment
  async setPrimaryAttachment(attachmentId) {
    try {
      const { error } = await supabase
        .rpc('set_primary_article_attachment', { attachment_uuid: attachmentId })

      if (error) throw error
      return true
    } catch (error) {
      console.error('Error setting primary attachment:', error)
      throw error
    }
  },

  // Record attachment download
  async recordAttachmentDownload(attachmentId) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      const downloadData = {
        attachment_id: attachmentId,
        user_id: user?.id || null,
        user_agent: navigator.userAgent
      }

      const { error } = await supabase
        .from('article_downloads')
        .insert([downloadData])

      if (error) console.warn('Error recording download:', error)
      return true
    } catch (error) {
      console.warn('Error recording attachment download:', error)
      return false
    }
  },

  // Get attachment download stats
  async getAttachmentDownloadStats(attachmentId) {
    try {
      const { data, error } = await supabase
        .from('article_downloads')
        .select('downloaded_at, user_id')
        .eq('attachment_id', attachmentId)
        .order('downloaded_at', { ascending: false })

      if (error) throw error
      
      const totalDownloads = data?.length || 0
      const uniqueUsers = new Set(data?.filter(d => d.user_id).map(d => d.user_id)).size
      
      return {
        totalDownloads,
        uniqueUsers,
        downloadHistory: data || []
      }
    } catch (error) {
      console.error('Error fetching download stats:', error)
      throw error
    }
  }
}

// Utility functions
export const estimateReadingTime = (content) => {
  if (!content) return 0
  
  // Remove HTML tags and count words
  const plainText = content.replace(/<[^>]*>/g, '')
  const wordCount = plainText.trim().split(/\s+/).length
  
  // Average reading speed is 200-250 words per minute
  const wordsPerMinute = 225
  const minutes = Math.ceil(wordCount / wordsPerMinute)
  
  return Math.max(1, minutes) // Minimum 1 minute
}

export const formatReadingTime = (minutes) => {
  if (minutes < 1) return 'Less than 1 minute'
  if (minutes === 1) return '1 minute'
  return `${minutes} minutes`
}

export const getArticleUrl = (article) => {
  if (!article) return '#'
  const slug = article.title.toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return `/articles/${article.id}/${slug}`
}

export const formatArticleDate = (dateString) => {
  if (!dateString) return 'No date available'
  
  const date = new Date(dateString)
  
  // Check if the date is invalid
  if (isNaN(date.getTime())) {
    return 'Invalid date'
  }
  
  const now = new Date()
  
  // Check if it's today
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  
  const diffTime = today.getTime() - dateOnly.getTime()
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
  
  if (diffDays === 0) {
    // Same day - show time
    const hours = date.getHours()
    const minutes = date.getMinutes()
    const timeStr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
    return `Today at ${timeStr}`
  }
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`
  if (diffDays < 365) return `${Math.ceil(diffDays / 30)} months ago`
  
  // For very old dates, show the actual date
  if (diffDays > 365) {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }
  
  return `${Math.ceil(diffDays / 365)} years ago`
}

export const getArticleExcerpt = (content, maxLength = 150) => {
  if (!content) return ''
  
  // Remove HTML tags
  const plainText = content.replace(/<[^>]*>/g, '')
  
  if (plainText.length <= maxLength) return plainText
  
  // Find the last complete word within the limit
  const truncated = plainText.substring(0, maxLength)
  const lastSpace = truncated.lastIndexOf(' ')
  
  return lastSpace > 0 ? truncated.substring(0, lastSpace) + '...' : truncated + '...'
}

export const validateArticle = (article) => {
  const errors = []
  
  if (!article.title || article.title.trim().length < 3) {
    errors.push('Title must be at least 3 characters long')
  }
  
  if (!article.content || article.content.trim().length < 50) {
    errors.push('Content must be at least 50 characters long')
  }
  
  if (!article.category) {
    errors.push('Category is required')
  }
  
  if (!article.difficulty) {
    errors.push('Difficulty level is required')
  }
  
  if (article.excerpt && article.excerpt.length > 300) {
    errors.push('Excerpt must be less than 300 characters')
  }
  
  return errors
}

export default articleService